<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  permissionRequest: {
    type: Object,
    required: true,
  },
});

const STATUS_PRESENTATION = {
  sending: {
    icon: 'i-ph-arrows-clockwise',
    containerClass: 'bg-n-blue-3 text-n-blue-11',
    iconClass: 'bg-n-blue-5 text-n-blue-11',
  },
  pending: {
    icon: 'i-ph-clock',
    containerClass: 'bg-n-blue-3 text-n-blue-11',
    iconClass: 'bg-n-blue-5 text-n-blue-11',
  },
  accepted: {
    icon: 'i-ph-check-circle',
    containerClass: 'bg-n-teal-3 text-n-teal-11',
    iconClass: 'bg-n-teal-5 text-n-teal-11',
  },
  rejected: {
    icon: 'i-ph-phone-slash',
    containerClass: 'bg-n-ruby-3 text-n-ruby-11',
    iconClass: 'bg-n-ruby-5 text-n-ruby-11',
  },
  temporarily_blocked: {
    icon: 'i-ph-hourglass-high',
    containerClass: 'bg-n-amber-3 text-n-amber-11',
    iconClass: 'bg-n-amber-5 text-n-amber-11',
  },
  failed: {
    icon: 'i-ph-warning-circle',
    containerClass: 'bg-n-ruby-3 text-n-ruby-11',
    iconClass: 'bg-n-ruby-5 text-n-ruby-11',
  },
  expired: {
    icon: 'i-ph-clock-counter-clockwise',
    containerClass: 'bg-n-slate-3 text-n-slate-11',
    iconClass: 'bg-n-slate-5 text-n-slate-11',
  },
};

const i18n = useI18n();
const { t } = i18n;

const status = computed(() => {
  const value = props.permissionRequest?.status;
  return typeof value === 'string' ? value.toLowerCase() : 'failed';
});

const presentation = computed(
  () => STATUS_PRESENTATION[status.value] || STATUS_PRESENTATION.failed
);
const statusLabel = computed(() => {
  switch (status.value) {
    case 'sending':
      return t('WHATSAPP_TEMPLATES.CALL_PERMISSION.STATUS.SENDING');
    case 'pending':
      return t('WHATSAPP_TEMPLATES.CALL_PERMISSION.STATUS.PENDING');
    case 'accepted':
      return t('WHATSAPP_TEMPLATES.CALL_PERMISSION.STATUS.ACCEPTED');
    case 'rejected':
      return t('WHATSAPP_TEMPLATES.CALL_PERMISSION.STATUS.REJECTED');
    case 'temporarily_blocked':
      return t('WHATSAPP_TEMPLATES.CALL_PERMISSION.STATUS.TEMPORARILY_BLOCKED');
    case 'expired':
      return t('WHATSAPP_TEMPLATES.CALL_PERMISSION.STATUS.EXPIRED');
    default:
      return t('WHATSAPP_TEMPLATES.CALL_PERMISSION.STATUS.FAILED');
  }
});

const deadline = computed(() => {
  if (status.value === 'temporarily_blocked') {
    return props.permissionRequest.blocked_until;
  }
  if (status.value === 'accepted' && !props.permissionRequest.permanent) {
    return props.permissionRequest.permission_expires_at;
  }
  if (['sending', 'pending'].includes(status.value)) {
    return props.permissionRequest.request_expires_at;
  }
  return null;
});

const resolvedLocale = computed(() => {
  const locale = i18n.locale?.value || i18n.locale || 'en';
  const normalized = locale.replace(/_/g, '-');
  return Intl.DateTimeFormat.supportedLocalesOf([normalized])[0] || 'en';
});

const formattedDeadline = computed(() => {
  if (!deadline.value) return '';

  const date = new Date(deadline.value);
  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat(resolvedLocale.value, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
});
</script>

<template>
  <section
    role="status"
    :data-status="status"
    class="flex gap-3 items-start p-3 rounded-lg"
    :class="presentation.containerClass"
  >
    <span
      aria-hidden="true"
      class="flex justify-center items-center rounded-full size-8 shrink-0"
      :class="presentation.iconClass"
    >
      <Icon
        :icon="presentation.icon"
        class="size-4"
        :class="{ 'animate-spin': status === 'sending' }"
      />
    </span>

    <div class="flex flex-col gap-1 min-w-0">
      <p class="text-sm font-medium text-n-slate-12">
        {{ statusLabel }}
      </p>
      <time
        v-if="formattedDeadline"
        data-testid="call-permission-deadline"
        :datetime="deadline"
        class="text-xs text-n-slate-11"
      >
        {{ formattedDeadline }}
      </time>
    </div>
  </section>
</template>
